import type { ChartDataPoint } from '../ChartData'
import type { ResolvedChartLineSeries } from '../ChartLine'

export type TooltipDatum = {
  x: string
  values: Array<{
    seriesKey: string
    label: string
    value: number
    color: string
    fillColor: string
    pointRadius: number
    lineWidth: number
  }>
}

const findNearestX = (
  series: Array<{ points: ChartDataPoint[] }>,
  xScale: (value: string) => number | undefined,
  pointerX: number,
): string | null => {
  let nearest: string | null = null
  let nearestDistance = Number.POSITIVE_INFINITY

  for (const seriesItem of series) {
    for (const point of seriesItem.points) {
      if (point.y == null) continue
      const scaled = xScale(point.x)
      if (scaled == null) continue
      const distance = Math.abs(scaled - pointerX)
      if (distance < nearestDistance) {
        nearestDistance = distance
        nearest = point.x
      }
    }
  }

  return nearest
}

const buildDatum = (
  x: string,
  series: ResolvedChartLineSeries[],
): TooltipDatum => ({
  x,
  values: series.flatMap(seriesItem => {
    const point = seriesItem.points.find(
      (candidate: ChartDataPoint) => candidate.x === x,
    )
    if (point?.y == null) return []
    return [
      {
        seriesKey: seriesItem.seriesKey,
        label: seriesItem.label ?? seriesItem.seriesKey,
        value: point.y,
        color: seriesItem.color,
        fillColor: seriesItem.fillColor,
        pointRadius: seriesItem.pointRadius,
        lineWidth: seriesItem.lineWidth,
      },
    ]
  }),
})

const hoverSeries = <T extends { visualization: string }>(series: T[]): T[] =>
  series.filter(
    seriesItem =>
      seriesItem.visualization === 'line' || seriesItem.visualization === 'bar',
  )

export const ChartInteractionUtils = {
  findNearestX,
  buildDatum,
  hoverSeries,
  /** @deprecated Use hoverSeries — includes line and bar marks. */
  lineSeriesOnly: hoverSeries,
}
